/**
 * CRS-18: the one price formatter, shared by the API (pricing.ts re-exports it), the course
 * builder and checkout UI. Pure — no database imports — so the SPA can import it directly.
 * Amounts are stored in the currency's minor units (paise, cents).
 */

const FALLBACK_MINOR_UNITS = 2;

/** Decimal places the currency uses (2 for INR/USD, 0 for JPY). */
export function currencyMinorUnits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? FALLBACK_MINOR_UNITS
    );
  } catch {
    return FALLBACK_MINOR_UNITS;
  }
}

export function majorToMinorUnits(major: number, currency: string): number {
  return Math.round(major * 10 ** currencyMinorUnits(currency));
}

export function minorToMajorUnits(minor: number, currency: string): number {
  const units = currencyMinorUnits(currency);
  return Number((minor / 10 ** units).toFixed(units));
}

/**
 * "$49.99", "₹4,999.50" — and "$600", "₹51,000" for whole amounts (was always rounded, so $49.99
 * showed as "$50").
 */
export function formatPriceDisplay(currency: string, amount: number, locale?: string): string {
  const units = currencyMinorUnits(currency);
  const whole = amount % 10 ** units === 0;
  return new Intl.NumberFormat(locale ?? (currency === "INR" ? "en-IN" : "en-US"), {
    style: "currency",
    currency,
    minimumFractionDigits: whole ? 0 : units,
    maximumFractionDigits: whole ? 0 : units,
  }).format(amount / 10 ** units);
}
